import React from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Typography, ThemeProvider, Link } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { Carousel } from '@one-thd/sui-carousel';

const ContentSection = ({
  children = null,
  isAutoplay = false,
  isCarousel = false,
  itemTheme = null,
  title = null,
  slidesXs = 1,
  slidesSm = null,
  slidesMd = null,
  slidesLg = null,
  slidesXl = null,
  spaceBetweenXs = 0,
  spaceBetweenSm = 16,
  spaceBetweenMd = 16,
  spaceBetweenLg = 16,
  spaceBetweenXl = 16,
  anchorId = null,
  theme = null,
  sectionCTAButton = null,
  disableGrid = false
}) => {

  const myItemTheme = itemTheme || theme;
  const contents = React.Children.map(children, (child, index) => {
    let updatedChild = child;

    if (isCarousel) {
      updatedChild = (
        <div
          key={`item-${index}`}
          className={classNames(
            'content-section__carousel',
            'sui-w-full sui-bg-primary sui-flex sui-flex-col'
          )}
        >
          {updatedChild}
        </div>
      );
    }

    if (myItemTheme) {
      updatedChild = (
        <ThemeProvider theme={getThemeByName(myItemTheme)}>
          {updatedChild}
        </ThemeProvider>
      );
    }

    return updatedChild;
  });

  // TODO: The below line is a temporary workaround for PromoVisualNavigation
  const isPromoVisNav = children[0]?.props?.componentClass === 'PromoVisualNavigation';
  const { callToAction, destinationLink } = sectionCTAButton || {};
  const sectionTitle = (
    <>
      {title && !isPromoVisNav && (
        <div className="sui-flex md:sui-flex-row sui-justify-between sui-py-4">
          <div className="sui-pb-4 md:sui-pb-0">
            <Typography
              variant="h2"
              weight="display"
              height="snug"
              color="primary"
              uppercase
            >
              {title}
            </Typography>
          </div>
          {destinationLink && callToAction && (
            <div className="sui-w-fit">
              <Link href={destinationLink}>
                {callToAction}
              </Link>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <ThemeProvider theme={getThemeByName(theme)}>
      <section
        id={anchorId}
        className={classNames(
          'sui-bg-primary sui-text-primary',
          { 'sui-p-4': !!theme }
        )}
      >
        {sectionTitle}
        {
          isCarousel
            ? (
              <Carousel
                autoplay={isAutoplay}
                peek={false}
                slidesPerGroup={slidesXs}
                slidesPerView={slidesXs}
                spaceBetween={spaceBetweenXs}
                hidePaginationOnOneOfOne
                breakpoints={{
                  sm: {
                    slidesPerView: slidesSm || 1,
                    slidesPerGroup: slidesSm || 1,
                    spaceBetween: spaceBetweenSm,
                  },
                  md: {
                    slidesPerView: slidesMd || 1,
                    slidesPerGroup: slidesMd || 1,
                    spaceBetween: spaceBetweenMd,
                  },
                  lg: {
                    slidesPerView: slidesLg || 1,
                    slidesPerGroup: slidesLg || 1,
                    spaceBetween: spaceBetweenLg,
                  },
                  xl: {
                    slidesPerView: slidesXl || 1,
                    slidesPerGroup: slidesXl || 1,
                    spaceBetween: spaceBetweenXl,
                  },
                }}
              >
                {contents}
              </Carousel>
            )
            : (
              <div className={classNames(
                { 'content-section__container sui-grid-cols-[repeat(auto-fit,minmax(23.5rem,1fr))]': !disableGrid },
                !disableGrid && 'sui-grid sui-gap-5'
              )}
              >
                {contents}
              </div>
            )
        }
      </section>
    </ThemeProvider>
  );
};

ContentSection.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  isAutoplay: PropTypes.bool,
  isCarousel: PropTypes.bool,
  itemTheme: PropTypes.string,
  spaceBetweenXs: PropTypes.number,
  spaceBetweenSm: PropTypes.number,
  spaceBetweenMd: PropTypes.number,
  spaceBetweenLg: PropTypes.number,
  spaceBetweenXl: PropTypes.number,
  title: PropTypes.string,
  slidesXs: PropTypes.number,
  slidesSm: PropTypes.number,
  slidesMd: PropTypes.number,
  slidesLg: PropTypes.number,
  slidesXl: PropTypes.number,
  anchorId: PropTypes.string,
  theme: PropTypes.string,
  sectionCTAButton: {
    callToAction: PropTypes.string,
    destinationLink: PropTypes.string
  },
  disableGrid: PropTypes.bool,
};

export { ContentSection };
